/* eslint-disable no-unused-vars */
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Typography,
  Grid,
  ButtonBase,
  Box,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'

import Loading from '../helpers/Loading'
import Widget from '../Widget'

import styles from './MyTeachingScheduleStyles'
import {
  fetchTeachingSchedule,
  selectTeachingScheduleCourseTerms,
  selectTeachingScheduleCurrentTerm,
  selectTeachingScheduleData,
  selectTeachingScheduleStatus,
  updateCurrentTerm,
} from './MyTeachingScheduleSlice'
import { logDashboardEvent } from '../../events'
import { ErrorMessage } from '../../DashboardComponents/ErrorMessages/ErrorMsg'
import IconPicker from '../../../apps/AppComponents/IconPicker/IconPicker'
import { filterDataByYearTerm } from '../../../../../utils/utils'

function MyTeachingSchedule({
  className,
  title,
  widgetId,
  showNote,
  noteMessage,
  notePosition,
  noteBackground,
  noteIcon,
  noteUrl,
  noteFontColor,
}) {
  const classes = styles()
  const dispatch = useDispatch()
  const widgetData = useSelector(selectTeachingScheduleData)
  const widgetStatus = useSelector(selectTeachingScheduleStatus)
  const currentTerm = useSelector(selectTeachingScheduleCurrentTerm)
  const courseTerms = useSelector(selectTeachingScheduleCourseTerms)

  React.useEffect(() => {
    dispatch(fetchTeachingSchedule())
  }, [])

  return (
    <Widget className={className}>
      <Widget.Header title={title} category="default" />
      <Widget.Content
        className={classes.container}
        showNote={showNote}
        noteMessage={noteMessage}
        notePosition={notePosition}
        noteBackground={noteBackground}
        noteIcon={noteIcon}
        noteUrl={noteUrl}
        noteFontColor={noteFontColor}
        addFooterStroke
        widgetId={widgetId}
      >
        {renderWidgetState(
          widgetStatus,
          widgetData,
          currentTerm,
          courseTerms,
          classes,
          widgetId,
          dispatch,
        )}
      </Widget.Content>
    </Widget>
  )
}

function renderWidgetState(
  widgetStatus,
  widgetData,
  currentTerm,
  courseTerms,
  classes,
  widgetId,
  dispatch,
) {
  switch (widgetStatus) {
    case 'fulfilled':
      return (
        <Loaded
          widgetData={widgetData}
          currentTerm={currentTerm}
          courseTerms={courseTerms}
          classes={classes}
          widgetId={widgetId}
          dispatch={dispatch}
        />
      )
    case 'pending':
      return <Loading />
    case 'error':
      return <ErrorMessage />
    default:
      return <ErrorMessage />
  }
}

function Loaded({
  widgetData,
  currentTerm,
  courseTerms,
  classes,
  widgetId,
  dispatch,
}) {
  const { tab } = useSelector((s) => s.myTeachingSchedule)
  const handleCourseTermChange = (e) => {
    dispatch(updateCurrentTerm(e.target.value))
  }
  const formatCourseTermToSeason = (term) => {
    switch (term) {
      case '1':
        return 'Spring'
      case '7':
        return 'Summer'
      case '9':
        return 'Fall'
      case '0':
        return 'Winter'
      case '5':
        return 'Winter'
      default:
        return 'Winter'
    }
  }
  if (!widgetData[tab] || widgetData[tab]?.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        marginLeft="auto"
        marginRight="auto"
        flexDirection="column"
        className={classes.noCoursesContainer}
      >
        <IconPicker iconName="check-circle" color="#00b339" size="5x" />
        <Typography variant="subtitle1">No Course Data</Typography>
      </Box>
    )
  }
  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className={classes.gridContainer}
    >
      <Grid item className={classes.selectContainer}>
        <InputLabel
          id="teaching-schedule-select"
          htmlFor="teaching-schedule-term"
        >
          Course Term
        </InputLabel>
        <Select
          labelId="teaching-courses-menu-select"
          value={currentTerm}
          onChange={handleCourseTermChange}
          className={classes.selector}
          inputProps={{
            name: 'teaching-course-terms',
            id: 'controlled-native',
          }}
        >
          {courseTerms && courseTerms.length > 0
            ? courseTerms.map((course, index) => (
                // eslint-disable-next-line
                <MenuItem key={index} value={index}>
                  {`${formatCourseTermToSeason(course.term)} `} {course.year}
                </MenuItem>
              ))
            : undefined}
        </Select>
      </Grid>
      <Grid item className={classes.courseGridContainer}>
        {filterDataByYearTerm(
          widgetData[tab],
          courseTerms[currentTerm].year,
          courseTerms[currentTerm].term,
        )?.map((course) => (
          <Grid
            item
            key={`${course.courseTitle} ${course.meetingDay} ${course.startTime} ${course.course}`}
            className={classes.courseGrid}
          >
            <CourseInfo
              course={course}
              classes={classes}
              widgetId={widgetId}
              dispatch={dispatch}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
}

function CourseInfo({ course, classes, widgetId, dispatch }) {
  const handleLinkClick = (link, buttonTitle) => {
    window.open(link, '_blank')
    if (widgetId && dispatch) {
      logDashboardEvent(dispatch, 'dashboardEvent', widgetId, buttonTitle, link)
    }
  }

  return (
    <Grid container className={classes.singleCourse} direction="column">
      <Grid item>
        <Typography variant="subtitle1">{course.courseTitle}</Typography>
      </Grid>
      <Grid item container direction="row" justifyContent="space-between">
        <Grid item className={classes.singleLine}>
          <Typography variant="body2">
            <b>Course #:</b> {course.course}
          </Typography>
        </Grid>
        <Grid item className={classes.sectionGrid}>
          <Typography variant="body2">
            <b>Section:</b> {course.sectionNo}
          </Typography>
        </Grid>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Enrollment:</b> {course.roster ? course.roster : 'n/a'}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Max Enrollment:</b>{' '}
          {course.maxEnrollment
            ? course.maxEnrollment.replace(/^0+/, '')
            : 'n/a'}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Registration Stop Point:</b>{' '}
          {course.registrationStopPoint
            ? course.registrationStopPoint.replace(/^0+/, '')
            : 'n/a'}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Time:</b> {course.meetingDay} {course.startTime} - {course.endTime}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Type:</b> {course.courseType ? course.courseType : 'n/a'}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Location:</b>{' '}
          {course.bldgInfo &&
          course.bldgInfo.bldgVM &&
          course.bldgInfo.bldgVM.bldgDescr ? (
            <>
              {course.bldgInfo.bldgURL ? (
                <>
                  <ButtonBase
                    className={classes.link}
                    component={Typography}
                    variant="body2"
                    onClick={() =>
                      handleLinkClick(course.bldgInfo.bldgURL, 'RutgersMap')
                    }
                  >
                    {course.bldgInfo.bldgVM.bldgDescr}
                  </ButtonBase>{' '}
                  (
                  {course.dcsUrl ? (
                    <ButtonBase
                      className={classes.link}
                      component={Typography}
                      variant="body2"
                      onClick={() => handleLinkClick(course.dcsUrl, 'DCS')}
                    >
                      {course.bldgInfo.bldgCd}-{course.roomno}
                    </ButtonBase>
                  ) : (
                    `${course.bldgInfo.bldgCd}-${course.roomno}`
                  )}
                  )
                </>
              ) : (
                <span>
                  {course.bldgInfo.bldgVM.bldgDescr} ({course.bldgInfo.bldgCd}-
                  {course.roomno})
                </span>
              )}
            </>
          ) : (
            'No location info'
          )}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        <Typography variant="body2">
          <b>Campus:</b>{' '}
          {course.campusName ? course.campusName : course.campusCode}
        </Typography>
      </Grid>
      <Grid item className={classes.singleLine}>
        {course.examCd && (
          <Typography variant="body2">
            <b>Final:</b> {course.examCd} - {course.examDay}
            {course.examTime && ` - ${course.examTime}`}
          </Typography>
        )}
      </Grid>
    </Grid>
  )
}

export default MyTeachingSchedule
